/**
 * ChildRisksTable — risks inherited from child TARAs (hierarchical
 * mode). Read-only data + Relevant checkbox + Justification input.
 * Treatment filter also applies. Mirror of ChildAttackPathsTable.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  DataTable,
  TableToolbar,
  TablePagination,
} from '../../../../components/ui/DataTable';
import FilterDrawer from '../../../../components/ui/DataTable/FilterDrawer';
import LevelBadge from '../../../../components/ui/LevelBadge';
import RelevanceCheckmark from '../../components/RelevanceCheckmark';
import RelevanceEditToggle from '../../components/RelevanceEditToggle';
import {
  getRiskValueColor,
  getTreatmentDecisionColor,
  getTreatmentPriorityColor,
} from '../../../../utils/severityStyle';
import {
  TREATMENT_DECISIONS,
  type RiskRecord,
} from './riskFieldSchema';
import styles from './ChildRisksTable.module.css';
import tableStyles from './RiskTable.module.css';

const DataTableAny = DataTable as unknown as React.ComponentType<any>;
const TableToolbarAny = TableToolbar as unknown as React.ComponentType<any>;
const TablePaginationAny = TablePagination as unknown as React.ComponentType<any>;
const FilterDrawerAny = FilterDrawer as unknown as React.ComponentType<any>;

const DEFAULT_PAGE_SIZE = 20;

const renderRiskValue = (row: ChildRiskView) => {
  const v = row.risk_value;
  if (v == null) return '';
  return <LevelBadge color={getRiskValueColor(v) ?? 'var(--color-status-neutral)'} label={String(v)} size="sm" />;
};
const renderTreatmentDecision = (row: ChildRiskView) =>
  row.treatment_decision ? (
    <LevelBadge
      color={getTreatmentDecisionColor(row.treatment_decision)}
      label={String(row.treatment_decision)}
      size="sm"
    />
  ) : '';
const renderTreatmentPriority = (row: ChildRiskView) =>
  row.treatment_priority ? (
    <LevelBadge
      color={getTreatmentPriorityColor(row.treatment_priority)}
      label={String(row.treatment_priority)}
      size="sm"
    />
  ) : '';

export interface ChildRiskView extends RiskRecord {}

export interface ChildRisksTableProps {
  risks: ChildRiskView[];
  onToggleRelevance: (riskId: string) => void;
  onJustificationChange: (riskId: string, justification: string) => void;
  disabled?: boolean;
  /** Persist relevance modifications on exit-edit. Story 15.F. */
  onSave?: () => Promise<void> | void;
}

export default function ChildRisksTable({
  risks,
  onToggleRelevance,
  onJustificationChange,
  disabled,
  onSave,
}: ChildRisksTableProps) {
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [decisionValues, setDecisionValues] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const liveEditing = editing && !disabled && !saving;

  const handleToggle = async () => {
    if (editing) {
      if (onSave) {
        setSaving(true);
        try { await onSave(); } finally { setSaving(false); }
      }
      setEditing(false);
    } else {
      setEditing(true);
    }
  };

  const decisionCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const d of TREATMENT_DECISIONS) counts[d.value] = 0;
    for (const r of risks) {
      const k = String(r.treatment_decision ?? '');
      if (k in counts) counts[k] += 1;
    }
    return counts;
  }, [risks]);

  const decisionFiltered = useMemo(
    () => (decisionValues.length === 0
      ? risks
      : risks.filter((r) => decisionValues.includes(String(r.treatment_decision ?? '')))),
    [risks, decisionValues],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return decisionFiltered;
    return decisionFiltered.filter((r) =>
      Object.values(r).some((v) =>
        v == null
          ? false
          : Array.isArray(v)
          ? v.join(' ').toLowerCase().includes(q)
          : String(v).toLowerCase().includes(q),
      ),
    );
  }, [decisionFiltered, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const paged = useMemo(
    () => filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page, pageSize],
  );

  const keyed = useMemo(
    () =>
      paged.map((r, i) => ({
        ...r,
        _rowKey: `${r.risk_id ?? 'row'}::${(page - 1) * pageSize + i}`,
      })),
    [paged, page, pageSize],
  );

  const columns = useMemo(
    () => [
      { key: 'risk_id', label: 'ID', sortable: true, width: '8%' },
      { key: 'asset_id', label: 'Asset', sortable: true, width: '10%' },
      { key: 'threat_id', label: 'Threat', sortable: true, width: '10%' },
      { key: 'risk_value', label: 'Value', sortable: true, width: '7%', render: renderRiskValue },
      { key: 'treatment_decision', label: 'Treatment', sortable: true, width: '10%', render: renderTreatmentDecision },
      { key: 'treatment_priority', label: 'Priority', sortable: true, width: '9%', render: renderTreatmentPriority },
      { key: 'treatment_justification', label: 'Justification', sortable: false, width: '21%' },
      {
        key: '_relevance',
        label: 'Relevant',
        sortable: false,
        align: 'center',
        width: '10%',
        render: (row: ChildRiskView) =>
          liveEditing ? (
            <input
              type="checkbox"
              className={styles.relevanceCheckbox}
              checked={row.is_relevant !== false}
              onChange={() => onToggleRelevance(row.risk_id)}
              onClick={(e) => e.stopPropagation()}
              aria-label={`Mark ${row.risk_id} as relevant`}
            />
          ) : (
            <RelevanceCheckmark relevant={row.is_relevant !== false} />
          ),
      },
      {
        key: '_justification',
        label: 'Justification',
        sortable: false,
        width: '19%',
        render: (row: ChildRiskView) =>
          liveEditing ? (
            <input
              type="text"
              className={styles.justificationInput}
              value={row.relevance_justification ?? ''}
              placeholder="Why? (optional)"
              onChange={(e) => onJustificationChange(row.risk_id, e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span>{row.relevance_justification || '—'}</span>
          ),
      },
    ],
    [onToggleRelevance, onJustificationChange, liveEditing],
  );

  const decisionFilter = {
    key: 'treatment_decision',
    label: 'Treatment',
    values: decisionValues,
    onChange: (next: string[]) => {
      setDecisionValues(next);
      setPage(1);
    },
    options: TREATMENT_DECISIONS.map((d) => ({
      value: d.value,
      label: d.label,
      count: decisionCounts[d.value] ?? 0,
    })),
  };

  return (
    <div className={tableStyles.tableShell}>
      <FilterDrawerAny
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={[decisionFilter]}
      />
      <DataTableAny
        columns={columns}
        data={keyed}
        rowKey="_rowKey"
        emptyMessage="No inherited risks from child TARAs."
        toolbar={
          <TableToolbarAny
            search={{
              value: search,
              onChange: (v: string) => { setSearch(v); setPage(1); },
              placeholder: 'Search inherited risks…',
            }}
            count={{ value: filtered.length, label: 'Inherited Risks' }}
            onFilterToggle={() => setFilterOpen((o) => !o)}
            filterButtonActive={filterOpen || decisionValues.length > 0}
            actions={
              <RelevanceEditToggle
                editing={liveEditing}
                onToggle={handleToggle}
                pending={saving}
                disabled={disabled}
              />
            }
          />
        }
        pagination={
          <TablePaginationAny
            currentPage={page}
            totalItems={filtered.length}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onItemsPerPageChange={(size: number) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        }
      />
    </div>
  );
}
